import React, { useState, useEffect } from "react";
import {
  Send,
  Phone,
  Mail,
  Building,
  CheckCircle,
} from "lucide-react";
import { companyInfo } from "../../data/mock";

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    service: "",
    message: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const services = [
    "Web Application Development",
    "Mobile App Development",
    "SaaS Product Development",
    "AI Tools Development",
    "Cloud Solutions",
    "Team Augmentation",
    "SEO & Digital Services",
    "Other",
  ];

  /* ================= PREFILL EMAIL (OPTIONAL) ================= */
  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem("user_email");
      if (savedEmail) {
        setFormData((prev) => ({ ...prev, email: savedEmail }));
      }
    } catch {}
  }, []);

  /* ================= HANDLE INPUT ================= */
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  /* ================= VALIDATION ================= */
  const validate = () => {
    if (!formData.name.trim()) return "Name is required";
    if (!formData.email.trim()) return "Email is required";
    if (!formData.message.trim()) return "Message is required";
    return null;
  };

  /* ================= HANDLE SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return; // prevent spam clicks

    const error = validate();
    if (error) {
      setErrorMsg(error);
      return;
    }

    setErrorMsg("");
    setLoading(true);

    try {
      const res = await fetch(
        "https://teamamtbsolution.com/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            source: "contact_form",
            page: window.location.pathname,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Submission failed");
      }

      /* ✅ SUCCESS */
      setIsSubmitted(true);

      // optional: store email for future autofill
      try {
        localStorage.setItem("user_email", formData.email);
      } catch {}

      // reset form except email
      setFormData({
        name: "",
        email: formData.email,
        phone: "",
        organization: "",
        service: "",
        message: "",
      });

      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (err) {
      console.error("Contact error:", err);
      setErrorMsg(err.message || "Server error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16">

          {/* LEFT */}
          <div>
            <h2 className="text-3xl font-bold mb-4">
              Let's Start Your Project
            </h2>

            <p className="text-gray-600 mb-8">
              Fill out the form and we’ll contact you within 24 hours.
            </p>

            <div className="space-y-4">
              <div className="bg-white p-6 shadow border rounded-2xl flex gap-3 items-center">
                <Phone />
                <a href={`tel:${companyInfo.phone}`}>
                  {companyInfo.phone}
                </a>
              </div>

              <div className="bg-white p-6 shadow border rounded-2xl flex gap-3 items-center">
                <Mail />
                <a href={`mailto:${companyInfo.email}`}>
                  {companyInfo.email}
                </a>
              </div>

              <div className="bg-white p-6 shadow border rounded-2xl flex gap-3 items-center">
                <Building />
                <p>{companyInfo.address}</p>
              </div>
            </div>
          </div>

          {/* RIGHT FORM */}
          <div className="bg-white rounded-3xl p-8 shadow-xl border">

            {isSubmitted ? (
              <div className="text-center py-12">
                <CheckCircle className="mx-auto text-green-500" size={50} />
                <h3 className="text-2xl font-bold mt-4">Thank You!</h3>
                <p className="text-gray-500">
                  We will contact you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* ERROR */}
                {errorMsg && (
                  <div className="bg-red-100 text-red-600 px-4 py-2 rounded">
                    {errorMsg}
                  </div>
                )}

                {/* NAME + EMAIL */}
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    name="name"
                    placeholder="Full Name *"
                    value={formData.name}
                    onChange={handleChange}
                    className="border p-3 rounded-xl w-full"
                  />

                  <input
                    name="email"
                    type="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={handleChange}
                    className="border p-3 rounded-xl w-full"
                  />
                </div>

                {/* PHONE + ORG */}
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    name="phone"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="border p-3 rounded-xl w-full"
                  />

                  <input
                    name="organization"
                    placeholder="Organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className="border p-3 rounded-xl w-full"
                  />
                </div>

                {/* SERVICE */}
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="border p-3 rounded-xl w-full"
                >
                  <option value="">Select Service</option>
                  {services.map((s, i) => (
                    <option key={i}>{s}</option>
                  ))}
                </select>

                {/* MESSAGE */}
                <textarea
                  name="message"
                  placeholder="Your message *"
                  value={formData.message}
                  onChange={handleChange}
                  className="border p-3 rounded-xl w-full"
                />

                {/* BUTTON */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 rounded-xl flex justify-center items-center gap-2 text-white transition ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-[#B8336A] to-[#2D6A6A]"
                  }`}
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>

              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;